import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import SearchInput from '../../components/ui/SearchInput';
import ResponsiveTable from '../../components/ui/ResponsiveTable';
import Card from '../../components/ui/Card';

const PaymentsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data for payments
  const payments = [
    { id: '1', invoice_number: 'INV-00245', customer: 'John Doe', account: 'Cash', amount: 1250000, payment_method: 'Cash', date: '2023-06-15' },
    { id: '2', invoice_number: 'INV-00244', customer: 'Jane Smith', account: 'Bank Account', amount: 850000, payment_method: 'Bank Transfer', date: '2023-06-14' },
    { id: '3', invoice_number: 'INV-00243', customer: 'Robert Johnson', account: 'Cash', amount: 2100000, payment_method: 'Cash', date: '2023-06-13' },
  ];

  const filteredPayments = payments.filter(payment => {
    return payment.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.account.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getFormattedAmount = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
        <Button variant="primary" onClick={() => navigate('/payments/new')}>
          Add New Payment
        </Button>
      </div>

      <div className="mb-6">
        <SearchInput
          placeholder="Search payments..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <ResponsiveTable
          headers={['Invoice Number', 'Customer', 'Account', 'Amount', 'Payment Method', 'Date', 'Actions']}
          rows={filteredPayments.map(payment => [
            payment.invoice_number,
            payment.customer,
            payment.account,
            getFormattedAmount(payment.amount),
            payment.payment_method,
            payment.date,
            <div className="flex space-x-2">
              <button
                onClick={() => navigate(`/payments/${payment.id}/edit`)}
                className="text-primary-600 hover:text-primary-900"
              >
                View
              </button>
            </div>
          ])}
          renderRow={(row, index) => {
            const payment = filteredPayments[index];
            return (
              <tr key={payment.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row[0]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[1]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[2]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[3]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[4]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[5]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[6]}
                </td>
              </tr>
            );
          }}
          renderMobileCard={(row, index) => {
            const payment = filteredPayments[index];
            return (
              <Card key={payment.id} className="mb-4">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        {row[0]}
                      </h3>
                      <div className="mt-2 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Customer</p>
                          <p className="text-sm font-medium text-gray-900">{row[1]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Account</p>
                          <p className="text-sm font-medium text-gray-900">{row[2]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Amount</p>
                          <p className="text-sm font-medium text-gray-900">{row[3]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Payment Method</p>
                          <p className="text-sm font-medium text-gray-900">{row[4]}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-sm text-gray-500">Date</p>
                          <p className="text-sm font-medium text-gray-900">{row[5]}</p>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => navigate(`/payments/${payment.id}/edit`)}
                        className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                      >
                        View
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          }}
          emptyMessage="No payments found"
        />
      </div>

      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Showing {filteredPayments.length} of {payments.length} payments
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" disabled>
            Previous
          </Button>
          <Button variant="outline" disabled>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentsList;